import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { captureError } from '@/lib/sentry';
import { desativarMembro, reativarMembro } from '@/services/organizations.service';
import { parseDesativarError } from '@/lib/member-deactivation';

function invalidarEquipe(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['profiles-com-roles'] });
  queryClient.invalidateQueries({ queryKey: ['prestadores'] });
  queryClient.invalidateQueries({ queryKey: ['plan-limits'] });
}

export function useDesativarMembro() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ userId }: { userId: string }) => desativarMembro({ userId }),
    onSuccess: () => {
      invalidarEquipe(queryClient);
      toast.success(t('team.deactivate.success'));
    },
    onError: (error: Error) => {
      const parsed = parseDesativarError(error);
      captureError(error, { extra: { hook: 'useDesativarMembro', code: parsed.code } });
      if (parsed.code === 'bloqueado_os_ativas') {
        toast.error(t('team.deactivate.blockedByOs', { count: parsed.count }));
      } else if (parsed.code === 'nao_pode_desativar_owner') {
        toast.error(t('team.deactivate.cannotOwner'));
      } else if (parsed.code === 'nao_pode_desativar_a_si_mesmo') {
        toast.error(t('team.deactivate.cannotSelf'));
      } else {
        toast.error(t('team.deactivate.error'));
      }
    },
  });
}

export function useReativarMembro() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ userId }: { userId: string }) => reativarMembro({ userId }),
    onSuccess: () => {
      invalidarEquipe(queryClient);
      toast.success(t('team.reactivate.success'));
    },
    onError: (error: Error) => {
      const parsed = parseDesativarError(error);
      captureError(error, { extra: { hook: 'useReativarMembro', code: parsed.code } });
      if (parsed.code === 'limite_plano_atingido') {
        toast.error(t('team.reactivate.limitReached'));
      } else {
        toast.error(t('team.reactivate.error'));
      }
    },
  });
}
